import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { baseQueryWithReauth } from './baseQuery';

export interface IDisponibilidadAlerta {
    tipo: 'RADA_FORZADA' | 'RADA_VOLUNTARIA_CON_COBRO' | 'MPO_DISPONIBLE' | 'RADA_NO_COMERCIAL';
    mensaje: string;
}

export interface IDisponibilidadZonasResponse {
    alertas: IDisponibilidadAlerta[];
}

export interface ICheckDisponibilidadZonasBody {
    idTemporada: number;
    idBuque: number;
    fechaDesde: string;
    fechaHasta: string;
    idTipoOperacion?: number;
}

export interface ValidarCaladoResponse {
    requiereAlertaCalado: boolean;
    cantidadBuquesSolapados: number;
}

export const bookingsApi = createApi({
    reducerPath: 'bookings',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Seasons', 'SeasonById', 'Bookings', 'BookingById', 'SeasonStatisticsById', 'ZoneBooking'],
    endpoints: (builder) => ({
        getSeasons: builder.query({
            query: (filters: any) => ({
                url: 'reservas/temporadas',
                params: !!Object.keys(filters)?.length ? filters : null
            }),
            providesTags: ['Seasons'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getSeasonById: builder.query({
            query: (seasonId: any) => ({
                url: `reservas/temporadas/${seasonId}`
            }),
            providesTags: ['SeasonById'],
            transformResponse: (response: { data: any }) => response?.data?.temporada
        }),
        getBookings: builder.query({
            query: ({ filters }: any) => ({
                url: 'reservas',
                params: filters
            }),
            providesTags: ['Bookings']
        }),
        getBookingById: builder.query({
            query: (bookingId: number) => ({
                url: `reservas/${bookingId}`
            }),
            providesTags: ['BookingById'],
            transformResponse: (response: { data: { reserva: any } }) => response?.data?.reserva
        }),
        addBooking: builder.mutation({
            query: (data: any) => ({
                url: 'reservas',
                method: 'POST',
                body: data
            })
        }),
        editBooking: builder.mutation({
            query: ({ data, bookingId }: any) => ({
                url: `reservas/${bookingId}`,
                body: data,
                method: 'PUT'
            }),
        }),
        checkDisponibilidadZonas: builder.mutation({
            query: (body: ICheckDisponibilidadZonasBody) => ({
                url: 'reservas/disponibilidad-zonas',
                method: 'POST',
                body
            }),
        }),
        addSeason: builder.mutation({
            query: (data: any) => ({
                url: 'reservas/temporadas',
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['SeasonById'])
        }),
        editSeason: builder.mutation({
            query: ({ data, seasonId }: any) => ({
                url: `reservas/temporadas/${seasonId}`,
                method: 'PUT',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['SeasonById'])
        }),
        deleteSeason: builder.mutation({
            query: (seasonId: string | number) => ({
                url: `reservas/temporadas/${seasonId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Seasons'])
        }),
        addSites: builder.mutation({
            query: (data: any) => ({
                url: 'reservas/sitios',
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Seasons', 'SeasonById'])
        }),
        editSite: builder.mutation({
            query: ({ data, siteId }: any) => ({
                url: `reservas/sitios/${siteId}`,
                method: 'PUT',
                body: data
            })
        }),
        deleteSite: builder.mutation({
            query: (siteId: any) => ({
                url: `reservas/sitios/${siteId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['SeasonById'])
        }),
        getSeasonStatisticsById: builder.query({
            query: ({ seasonId }: { seasonId: number }) => ({
                url: `reservas/estadisticas/reservas-by-temporada/${seasonId}`
            }),
            providesTags: ['SeasonStatisticsById'],
            transformResponse: (response: { data: { estadisticas: any } }) =>
                response?.data?.estadisticas
        }),
        submitBookingAction: builder.mutation({
            query: ({ action, bookingId, nota }: { action: string; bookingId: string; nota?: string }) => ({
                    url: `reservas/${bookingId}/accion/${action}`,
                    method: 'POST',
                    ...(nota && { body: { nota } })
                }),
            invalidatesTags: (_, error) => (error ? [] : ['Bookings', 'BookingById'])
        }),
        getBookingInvoice: builder.query({
            query: (bookingId: any) => ({
                url: `reservas/${bookingId}/facturas`
            }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        liquidateBooking: builder.mutation({
            query: ({ bookingId, data }: any) => ({
                url: `reservas/${bookingId}/facturas/liquidar`,
                method: 'POST',
                body: data
            })
        }),
        generateBookingRates: builder.mutation({
            query: ({ bookingId }: { bookingId: string | number }) => ({
                url: `reservas/${bookingId}/facturas/tasas/generar`,
                method: 'POST'
            }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        downloadFile: builder.query({
            query: (bookingId) => ({
                url: `reservas/${bookingId}/pdf`
            }),
            transformResponse: (response: { data: { pdf: any } }) => response?.data?.pdf
        }),
        getFacturaArchivo: builder.query<{ url: string; expiresAt: string }, number | string>({
            query: (bookingId) => ({
                url: `reservas/${bookingId}/facturas/archivo`
            }),
            transformResponse: (response: { data: { url: string; expiresAt: string } }) =>
                response?.data
        }),
        getComprobanteArchivo: builder.query<{ url: string; expiresAt: string }, number | string>({
            query: (bookingId) => ({
                url: `reservas/${bookingId}/comprobante/archivo`
            }),
            transformResponse: (response: { data: { url: string; expiresAt: string } }) =>
                response?.data
        }),
        adjuntarComprobante: builder.mutation({
            query: ({ bookingId, data }: { bookingId: string | number; data: FormData }) => ({
                url: `reservas/${bookingId}/comprobante`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['BookingById', 'Bookings'])
        }),
        validarPago: builder.mutation({
            query: ({ bookingId }: { bookingId: string | number }) => ({
                url: `reservas/${bookingId}/acciones/validar-pago`,
                method: 'POST',
                body: {}
            }),
            invalidatesTags: (_, error) => (error ? [] : ['BookingById', 'Bookings'])
        }),
        revisarComprobantePago: builder.mutation({
            query: ({ bookingId, data }: { bookingId: string | number; data: { motivo: string } }) => ({
                url: `reservas/${bookingId}/acciones/revisar-comprobante-pago`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['BookingById', 'Bookings'])
        }),
        validarCalado: builder.mutation<ValidarCaladoResponse, { bookingId: string | number }>({
            query: ({ bookingId }) => ({
                url: `reservas/${bookingId}/validar-calado`,
                method: 'POST',
                body: {}
            }),
            transformResponse: (response: { data: ValidarCaladoResponse }) => response.data
        }),
    })
});

export const {
    useLazyDownloadFileQuery,
    useLazyGetFacturaArchivoQuery,
    useLazyGetComprobanteArchivoQuery,
    useGenerateBookingRatesMutation,
    useLiquidateBookingMutation,
    useLazyGetBookingInvoiceQuery,
    useGetBookingInvoiceQuery,
    useSubmitBookingActionMutation,
    useEditSiteMutation,
    useDeleteSiteMutation,
    useDeleteSeasonMutation,
    useEditSeasonMutation,
    useEditBookingMutation,
    useLazyGetBookingByIdQuery,
    useGetSeasonByIdQuery,
    useLazyGetSeasonByIdQuery,
    useGetSeasonStatisticsByIdQuery,
    useAddSitesMutation,
    useAddSeasonMutation,
    useGetSeasonsQuery,
    useGetBookingsQuery,
    useGetBookingByIdQuery,
    useAddBookingMutation,
    useAdjuntarComprobanteMutation,
    useValidarPagoMutation,
    useRevisarComprobantePagoMutation,
    useCheckDisponibilidadZonasMutation,
    useValidarCaladoMutation,
} = bookingsApi;
